import React, { useState, useCallback, useEffect, useRef } from "react";
import FormCard from "../FormComponents/FormCard";
import FormGroup from "../FormComponents/FormGroup";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import SignatureSection from "../FormComponents/SignatureSection";
import PaymentSection from "../FormComponents/PaymentSection";
import Icon from "../Icons/Icon";
import AlertBanner from "../FormComponents/AlertBanner";

/**
 * Step 9: Sponsorship Opportunities
 *
 * This step allows exhibitors to select and purchase various sponsorship opportunities:
 * - Water cooler and branded water bottle sponsorship
 * - Coffee/Snack bar sponsorship
 * - Retailer panel sponsorship
 * - Open House branded bag sponsorship
 * - Wednesday night event sponsorships
 */
const Step9 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
}) => {
  const [formData, setFormData] = useState({
    participate: initialContract?.participate || "",
    showEntranceBannerQty: initialContract?.showEntranceBannerQty ?? 0,
    diningRoomEdgeBannerQty: initialContract?.diningRoomEdgeBannerQty ?? 0,
    waterQty: initialContract?.waterQty ?? 0,
    coffeeQty: initialContract?.coffeeQty ?? 0,
    retailerQty: initialContract?.retailerQty ?? 0,
    bagQty: initialContract?.bagQty ?? 0,
    barQty: initialContract?.barQty ?? 0,
    platinumQty: initialContract?.platinumQty ?? 0,
    notes: initialContract?.notes || "",
    paymentMethod: initialContract?.paymentMethod || "",
    invoiceAddressText: initialContract?.invoiceAddressText || "",
    signatureName: initialContract?.signatureName || "",
    signatureDate: initialContract?.signatureDate || "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
  });

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  const qtys = {
    showEntranceBanner: formData.showEntranceBannerQty,
    diningRoomEdgeBanner: formData.diningRoomEdgeBannerQty,
    water: formData.waterQty,
    coffee: formData.coffeeQty,
    retailer: formData.retailerQty,
    bag: formData.bagQty,
    bar: formData.barQty,
    platinum: formData.platinumQty,
  };

  const setQtys = (updateFn) => {
    const updated = updateFn(qtys);
    Object.keys(updated).forEach((key) => {
      updateField(`${key}Qty`, updated[key]);
    });
  };

  const sponsorships = [
    {
      key: "showEntranceBanner",
      label: "Show Entrance Banner",
      price: 400,
    },
    {
      key: "diningRoomEdgeBanner",
      label: "Dining Room Edge Banner",
      price: 400,
    },
    {
      key: "water",
      label: "2 Day Water Cooler & Branded Water Bottle Sponsorship (Only 5 Available)",
      price: 1500,
      limit: 5,
    },
    {
      key: "coffee",
      label: "Coffee/Snack Bar Sponsorship (Only 4 Available!)",
      price: 2500,
      limit: 4,
    },
    {
      key: "bag",
      label: "Open House Branded Bag Sponsorship (Up to 4 brands/logos on 1 bag)",
      price: 2800,
    },
    {
      key: "bar",
      label: "Wednesday Night Event Bar Sponsors (Only 4 Available!)",
      price: 3000,
      limit: 4,
    },
    {
      key: "platinum",
      label: "Wednesday Night Event Platinum Sponsor (Only 1 Available!)",
      price: 5000,
      limit: 1,
    },
  ];

  const total = sponsorships.reduce((s, sp) => {
    return s + sp.price * (qtys[sp.key] || 0);
  }, 0);

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Sponsorship Opportunities</h2>
          <p className="form-page-subtitle">Trade Show Sponsorships</p>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        <FormCard
          title="Available Sponsorships"
          icon={<Icon name="award" size={14} />}
        >
          <div
            className="radio-group"
            style={{ marginBottom: errors.participate ? 4 : 14 }}
          >
            <label className="custom-radio">
              <input
                type="radio"
                name="participate"
                value="yes"
                checked={formData.participate === "yes"}
                onChange={() => updateField("participate", "yes")}
              />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input
                type="radio"
                name="participate"
                value="no"
                checked={formData.participate === "no"}
                onChange={() => updateField("participate", "no")}
              />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.participate && (
            <span className="field-error" style={{ display: "block", marginBottom: 12 }}>
              {errors.participate}
            </span>
          )}
          {formData.participate === "yes" && (
            <>
              <table className="contract-table">
                <thead>
                  <tr>
                    <th>Enter Order Qty</th>
                    <th>Sponsorship Opportunity</th>
                    <th>EA Price</th>
                    <th>Sub Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sponsorships.map((sp) => {
                    const qty = qtys[sp.key] || 0;
                    return (
                      <tr key={sp.key}>
                        <td>
                          <input
                            className="form-input"
                            type="number"
                            min="0"
                            max={sp.limit}
                            value={qty || ""}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              setQtys((p) => ({
                                ...p,
                                [sp.key]: parseInt(e.target.value) || 0,
                              }))
                            }
                            style={{ width: 80 }}
                          />
                        </td>
                        <td>{sp.label}</td>
                        <td style={{ fontWeight: 600 }}>
                          ${sp.price.toLocaleString()}
                        </td>
                        <td style={{ fontWeight: 700, color: "var(--primary)" }}>
                          ${(sp.price * qty).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <td colSpan={3}>
                      <strong>Total Estimated Cost of Show Sponsorships:</strong>
                    </td>
                    <td>
                      <strong>${total.toLocaleString()}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <FormGroup label="Notes:" style={{ marginTop: 16 }}>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                />
              </FormGroup>
              {errors.sponsorshipQty && (
                <span className="field-error" style={{ display: "block", marginTop: 8 }}>
                  {errors.sponsorshipQty}
                </span>
              )}
            </>
          )}
        </FormCard>

        {formData.participate === "yes" && (
        <FormCard title="Payment Agreement" icon={<Icon name="card" size={14} />}>
          <p
            style={{
              fontSize: 13,
              marginBottom: 12,
              color: "var(--neutral-600)",
            }}
          >
            The agreed upon sponsorship fee shall be paid in full to Arett Sales,
            by check or credit card only, on or before 8/12/2026. My signature
            below indicates that I have read and consent to the details of this
            agreement.
          </p>
          <PaymentSection
            title="Payment Information (after 07/01/2026 payment must be made via check or credit card)"
            paymentMethod={formData.paymentMethod}
            invoiceAddressText={formData.invoiceAddressText}
            onPaymentMethodChange={(val) => updateField("paymentMethod", val)}
            onAddressChange={(val) => updateField("invoiceAddressText", val)}
          />
          {errors.paymentMethod && (
            <span className="field-error" style={{ marginTop: 8, display: "block" }}>{errors.paymentMethod}</span>
          )}
          <div className="section-divider" />
          <SignatureSection
            legal={false}
            name={formData.signatureName}
            date={formData.signatureDate}
            acknowledge={formData.signatureAcknowledge}
            onNameChange={(name) => updateField("signatureName", name)}
            onDateChange={(date) => updateField("signatureDate", date)}
            onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
          />
          {(errors.signatureName || errors.signatureDate) && (
            <div className="step-error-summary" style={{ marginTop: 8 }}>
              {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
              {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
            </div>
          )}
        </FormCard>
        )}

        <div className="form-footer-note">
          <strong>PLEASE PRINT THIS OUT FOR YOUR RECORDS</strong>
          <br />
          Please refer to vendor portal for any and all updates. Email updates
          will also be sent so be sure to whitelist all @arett.com domains
        </div>
      </div>
    </fieldset>
  );
};

export default Step9;
